import {
	Card,
	Form,
	Input,
	Button,
	Select,
	Row,
	Col,
	Typography,
	InputNumber,
	message,
	notification,
	Tabs,
	Empty,
	Space,
	Table,
} from "antd";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppContext from "../../store/app.context";
import { requestGetServiceGroupList, requestCreateService } from "../../services/admin.service";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { PiArrowLeftDuotone } from "react-icons/pi";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;

// Constants based on schema
const STATUS_OPTIONS = [
	{ label: "Active", value: "ACTIVE" },
	{ label: "Inactive", value: "INACTIVE" },
];

const FEATURE_STATUS_OPTIONS = [
	{ label: "Included", value: "INCLUDED" },
	{ label: "Excluded", value: "EXCLUDED" },
];

const PRICE_UNIT_OPTIONS = [
	{ label: "GB", value: "GB" },
	{ label: "TB", value: "TB" },
	{ label: "User", value: "USER" },
	{ label: "Instance", value: "INSTANCE" },
	{ label: "Hour", value: "HOUR" },
];

const PRICE_INTERVAL_OPTIONS = [
	{ label: "Hour", value: "HOUR" },
	{ label: "Day", value: "DAY" },
	{ label: "Week", value: "WEEK" },
	{ label: "Month", value: "MONTH" },
	{ label: "Quarter", value: "QUARTER" },
	{ label: "Semester", value: "SEMESTER" },
	{ label: "Year", value: "YEAR" },
];

const PRICE_TYPE_OPTIONS = [
	{ label: "Fixed", value: "FIXED" },
	{ label: "Percentage", value: "PERCENTAGE" },
];

function AddServicePage() {
	const context = useContext(AppContext);
	const navigate = useNavigate();
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [serviceGroups, setServiceGroups] = useState([]);

	// State for dynamic arrays
	const [features, setFeatures] = useState([]);
	const [plans, setPlans] = useState([]);
	const [regions, setRegions] = useState([]);
	const [addons, setAddons] = useState([]);

	useEffect(() => {
		document.title = "Cloudiva Console | Add Service";
		fetchServiceGroups();
	}, []);

	const fetchServiceGroups = () => {
		requestGetServiceGroupList({
			limit: 100,
			order: "name:asc",
		})
			.then((response) => {
				setServiceGroups(response.data.data);
			})
			.catch((error) => {
				notification.error({
					message: "Error",
					description: "Failed to fetch service groups",
				});
			});
	};

	const handleSubmit = () => {
		form
			.validateFields()
			.then((values) => {
				setLoading(true);

				// Process the form data to match the schema
				const serviceData = {
					groupId: values.groupId,
					code: values.code,
					name: values.name,
					description: values.description,
					status: values.status || "ACTIVE",
					features: features.map((f) => ({
						code: f.code,
						name: f.name,
						description: f.description,
						status: f.status,
					})),
					plans: plans.map((p) => ({
						code: p.code,
						name: p.name,
						description: p.description,
						status: p.status,
						features: p.features || [],
						price: {
							amount: p.priceAmount || 0,
							unit: p.priceUnit,
							interval: p.priceInterval,
						},
					})),
					regions: regions.map((r) => ({
						code: r.code,
						name: r.name,
						price: {
							amount: r.priceAmount || 0,
							type: r.priceType || "PERCENTAGE",
						},
					})),
					addons: addons.map((a) => ({
						code: a.code,
						name: a.name,
						description: a.description,
						status: a.status,
						price: {
							amount: a.priceAmount || 0,
							unit: a.priceUnit,
							type: a.priceType || "PERCENTAGE",
						},
					})),
					metadata: values.metadata ? JSON.parse(values.metadata) : {},
				};

				requestCreateService(serviceData)
					.then((response) => {
						message.success("Service created successfully!");
						navigate("/services");
					})
					.catch((error) => {
						console.error("Error creating service:", error);
						if (error.response?.data?.message) {
							notification.error({
								message: "Error",
								description: error.response.data.message,
							});
						} else {
							notification.error({
								message: "Error",
								description: "Failed to create service. Please try again.",
							});
						}
					})
					.finally(() => {
						setLoading(false);
					});
			})
			.catch((error) => {
				console.error("Validation failed:", error);
			});
	};

	// Feature management functions
	const handleFeature = {
		add: () => {
			const newFeature = {
				id: Date.now(),
				code: "",
				name: "",
				description: "",
				status: "INCLUDED",
			};
			setFeatures([...features, newFeature]);
		},
		update: (id, field, value) => {
			const updatedFeatures = features.map((feature) =>
				feature.id === id ? { ...feature, [field]: value } : feature
			);
			setFeatures(updatedFeatures);
		},
		remove: (id) => {
			setFeatures(features.filter((feature) => feature.id !== id));
		},
	};

	// Plan management functions
	const handlePlan = {
		add: () => {
			const newPlan = {
				id: Date.now(),
				code: "",
				name: "",
				description: "",
				status: "ACTIVE",
				features: [],
				priceAmount: 0,
				priceUnit: "",
				priceInterval: "MONTH",
			};
			setPlans([...plans, newPlan]);
		},
		update: (id, field, value) => {
			const updatedPlans = plans.map((plan) =>
				plan.id === id ? { ...plan, [field]: value } : plan
			);
			setPlans(updatedPlans);
		},
		remove: (id) => {
			setPlans(plans.filter((plan) => plan.id !== id));
		},
		updateFeatures: (planId, featureCode, field, value) => {
			setPlans(
				plans.map((p) =>
					p.id === planId
						? {
								...p,
								features: (p.features || []).map((f) =>
									f.code === featureCode ? { ...f, [field]: value } : f
								),
						  }
						: p
				)
			);
		},
		addFeature: (planId, feature) => {
			setPlans(
				plans.map((p) =>
					p.id === planId
						? {
								...p,
								features: [
									...(p.features || []),
									{
										code: feature.code,
										name: feature.name,
										value: "",
										status: "INCLUDED",
									},
								],
						  }
						: p
				)
			);
		},
		removeFeature: (planId, featureCode) => {
			setPlans(
				plans.map((p) =>
					p.id === planId
						? {
								...p,
								features: (p.features || []).filter((f) => f.code !== featureCode),
						  }
						: p
				)
			);
		},
	};

	// Region management functions
	const handleRegion = {
		add: () => {
			const newRegion = {
				id: Date.now(),
				code: "",
				name: "",
				priceAmount: 0,
				priceType: "PERCENTAGE",
			};
			setRegions([...regions, newRegion]);
		},
		update: (id, field, value) => {
			const updatedRegions = regions.map((region) =>
				region.id === id ? { ...region, [field]: value } : region
			);
			setRegions(updatedRegions);
		},
		remove: (id) => {
			setRegions(regions.filter((region) => region.id !== id));
		},
	};

	// Addon management functions
	const handleAddon = {
		add: () => {
			const newAddon = {
				id: Date.now(),
				code: "",
				name: "",
				description: "",
				status: "INCLUDED",
				priceAmount: 0,
				priceUnit: "",
				priceType: "PERCENTAGE",
			};
			setAddons([...addons, newAddon]);
		},
		update: (id, field, value) => {
			const updatedAddons = addons.map((addon) =>
				addon.id === id ? { ...addon, [field]: value } : addon
			);
			setAddons(updatedAddons);
		},
		remove: (id) => {
			setAddons(addons.filter((addon) => addon.id !== id));
		},
	};

	const tabItems = [
		{
			key: "1",
			label: "Basic Information",
			children: (
				<Card title="Basic Information">
					<Row gutter={16}>
						<Col span={12}>
							<Form.Item
								label="Service Group"
								name="groupId"
								rules={[{ required: true, message: "Please select a service group" }]}>
								<Select placeholder="Select service group">
									{serviceGroups.map((group) => (
										<Option key={group._id} value={group._id}>
											{group.name}
										</Option>
									))}
								</Select>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								label="Service Code"
								name="code"
								rules={[{ required: true, message: "Please enter service code" }]}>
								<Input placeholder="Enter service code" />
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={16}>
						<Col span={12}>
							<Form.Item
								label="Service Name"
								name="name"
								rules={[{ required: true, message: "Please enter service name" }]}>
								<Input placeholder="Enter service name" />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								label="Status"
								name="status"
								rules={[{ required: true, message: "Please select status" }]}>
								<Select placeholder="Select status">
									{STATUS_OPTIONS.map((option) => (
										<Option key={option.value} value={option.value}>
											{option.label}
										</Option>
									))}
								</Select>
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={16}>
						<Col span={24}>
							<Form.Item label="Description" name="description">
								<Input.TextArea rows={4} placeholder="Enter service description" />
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={16}>
						<Col span={24}>
							<Form.Item label="Metadata (JSON)" name="metadata">
								<Input.TextArea rows={6} placeholder="Enter metadata as JSON (optional)" />
							</Form.Item>
						</Col>
					</Row>
				</Card>
			),
		},
		{
			key: "2",
			label: "Features",
			children: (
				<Card
					title="Service Features"
					extra={
						<Button type="primary" onClick={handleFeature.add} icon={<PlusOutlined />}>
							Add Feature
						</Button>
					}>
					{features.map((feature) => (
						<Card key={feature.id} size="small" style={{ marginBottom: 16 }}>
							<Row gutter={16} align="middle">
								<Col span={5}>
									<Input
										placeholder="Feature code"
										value={feature.code}
										onChange={(e) => handleFeature.update(feature.id, "code", e.target.value)}
									/>
								</Col>
								<Col span={6}>
									<Input
										placeholder="Feature name"
										value={feature.name}
										onChange={(e) => handleFeature.update(feature.id, "name", e.target.value)}
									/>
								</Col>
								<Col span={8}>
									<Input
										placeholder="Description"
										value={feature.description}
										onChange={(e) =>
											handleFeature.update(feature.id, "description", e.target.value)
										}
									/>
								</Col>
								<Col span={3}>
									<Select
										value={feature.status}
										onChange={(value) => handleFeature.update(feature.id, "status", value)}
										style={{ width: "100%" }}>
										{FEATURE_STATUS_OPTIONS.map((option) => (
											<Option key={option.value} value={option.value}>
												{option.label}
											</Option>
										))}
									</Select>
								</Col>
								<Col span={2}>
									<Button
										type="text"
										danger
										icon={<DeleteOutlined />}
										onClick={() => handleFeature.remove(feature.id)}
									/>
								</Col>
							</Row>
						</Card>
					))}
					{features.length === 0 && <Empty description="No features added yet" />}
				</Card>
			),
		},
		{
			key: "3",
			label: "Plans",
			children: (
				<Card
					title="Service Plans"
					extra={
						<Button type="primary" onClick={handlePlan.add} icon={<PlusOutlined />}>
							Add Plan
						</Button>
					}>
					{plans.map((plan) => (
						<Card key={plan.id} size="small" style={{ marginBottom: 16 }}>
							<Row gutter={16} style={{ marginBottom: 12 }}>
								<Col span={4}>
									<Input
										placeholder="Plan code"
										value={plan.code}
										onChange={(e) => handlePlan.update(plan.id, "code", e.target.value)}
									/>
								</Col>
								<Col span={5}>
									<Input
										placeholder="Plan name"
										value={plan.name}
										onChange={(e) => handlePlan.update(plan.id, "name", e.target.value)}
									/>
								</Col>
								<Col span={6}>
									<Input
										placeholder="Description"
										value={plan.description}
										onChange={(e) => handlePlan.update(plan.id, "description", e.target.value)}
									/>
								</Col>
								<Col span={3}>
									<Select
										value={plan.status}
										onChange={(value) => handlePlan.update(plan.id, "status", value)}
										style={{ width: "100%" }}>
										{STATUS_OPTIONS.map((option) => (
											<Option key={option.value} value={option.value}>
												{option.label}
											</Option>
										))}
									</Select>
								</Col>
								<Col span={4}>
									<InputNumber
										placeholder="Price"
										value={plan.priceAmount}
										onChange={(value) => handlePlan.update(plan.id, "priceAmount", value || 0)}
										style={{ width: "100%" }}
										min={0}
									/>
								</Col>
								<Col span={2}>
									<Button
										type="text"
										danger
										icon={<DeleteOutlined />}
										onClick={() => handlePlan.remove(plan.id)}
									/>
								</Col>
							</Row>
							<Row gutter={16} style={{ marginBottom: 12 }}>
								<Col span={6}>
									<Select
										placeholder="Price unit"
										value={plan.priceUnit}
										onChange={(value) => handlePlan.update(plan.id, "priceUnit", value)}
										style={{ width: "100%" }}>
										{PRICE_UNIT_OPTIONS.map((option) => (
											<Option key={option.value} value={option.value}>
												{option.label}
											</Option>
										))}
									</Select>
								</Col>
								<Col span={6}>
									<Select
										placeholder="Price interval"
										value={plan.priceInterval}
										onChange={(value) => handlePlan.update(plan.id, "priceInterval", value)}
										style={{ width: "100%" }}>
										{PRICE_INTERVAL_OPTIONS.map((option) => (
											<Option key={option.value} value={option.value}>
												{option.label}
											</Option>
										))}
									</Select>
								</Col>
							</Row>
							<div style={{ marginTop: 12 }}>
								<Text strong>Plan Features:</Text>
								<div style={{ marginTop: 8 }}>
									<Table
										size="small"
										pagination={false}
										dataSource={plan.features || []}
										rowKey="code"
										columns={[
											{
												title: "Code",
												dataIndex: "code",
												key: "code",
												width: 120,
											},
											{
												title: "Name",
												dataIndex: "name",
												key: "name",
												width: 150,
											},
											{
												title: "Value",
												dataIndex: "value",
												key: "value",
												width: 120,
												render: (text, record) => (
													<Input
														size="small"
														value={text}
														onChange={(e) =>
															handlePlan.updateFeatures(
																plan.id,
																record.code,
																"value",
																e.target.value
															)
														}
														placeholder="e.g., 2GB, 100 users"
													/>
												),
											},
											{
												title: "Status",
												dataIndex: "status",
												key: "status",
												width: 120,
												render: (text, record) => (
													<Select
														size="small"
														value={text}
														onChange={(value) =>
															handlePlan.updateFeatures(plan.id, record.code, "status", value)
														}
														style={{ width: "100%" }}>
														{FEATURE_STATUS_OPTIONS.map((option) => (
															<Option key={option.value} value={option.value}>
																{option.label}
															</Option>
														))}
													</Select>
												),
											},
											{
												title: "Action",
												key: "action",
												width: 80,
												render: (_, record) => (
													<Button
														type="text"
														danger
														size="small"
														icon={<DeleteOutlined />}
														onClick={() => handlePlan.removeFeature(plan.id, record.code)}
													/>
												),
											},
										]}
										footer={() => (
											<Select
												placeholder="Add a feature to this plan"
												style={{ width: "100%" }}
												value={null}
												onChange={(featureCode) => {
													const feature = features.find((f) => f.code === featureCode);
													if (feature) {
														handlePlan.addFeature(plan.id, feature);
													}
												}}>
												{features
													.filter(
														(feature) =>
															!(plan.features || []).some((pf) => pf.code === feature.code)
													)
													.map((feature) => (
														<Option key={feature.code} value={feature.code}>
															{feature.name || feature.code}
														</Option>
													))}
											</Select>
										)}
									/>
								</div>
							</div>
						</Card>
					))}
					{plans.length === 0 && <Empty description="No plans added yet" />}
				</Card>
			),
		},
		{
			key: "4",
			label: "Regions",
			children: (
				<Card
					title="Service Regions"
					extra={
						<Button type="primary" onClick={handleRegion.add} icon={<PlusOutlined />}>
							Add Region
						</Button>
					}>
					{regions.map((region) => (
						<Card key={region.id} size="small" style={{ marginBottom: 16 }}>
							<Row gutter={16} align="middle">
								<Col span={6}>
									<Input
										placeholder="Region code"
										value={region.code}
										onChange={(e) => handleRegion.update(region.id, "code", e.target.value)}
									/>
								</Col>
								<Col span={8}>
									<Input
										placeholder="Region name"
										value={region.name}
										onChange={(e) => handleRegion.update(region.id, "name", e.target.value)}
									/>
								</Col>
								<Col span={4}>
									<InputNumber
										placeholder="Price amount"
										value={region.priceAmount}
										onChange={(value) => handleRegion.update(region.id, "priceAmount", value || 0)}
										style={{ width: "100%" }}
										min={0}
									/>
								</Col>
								<Col span={4}>
									<Select
										placeholder="Price type"
										value={region.priceType}
										onChange={(value) => handleRegion.update(region.id, "priceType", value)}
										style={{ width: "100%" }}>
										{PRICE_TYPE_OPTIONS.map((option) => (
											<Option key={option.value} value={option.value}>
												{option.label}
											</Option>
										))}
									</Select>
								</Col>
								<Col span={2}>
									<Button
										type="text"
										danger
										icon={<DeleteOutlined />}
										onClick={() => handleRegion.remove(region.id)}
									/>
								</Col>
							</Row>
						</Card>
					))}
					{regions.length === 0 && <Empty description="No regions added yet" />}
				</Card>
			),
		},
		{
			key: "5",
			label: "Add-ons",
			children: (
				<Card
					title="Service Add-ons"
					extra={
						<Button type="primary" onClick={handleAddon.add} icon={<PlusOutlined />}>
							Add Add-on
						</Button>
					}>
					{addons.map((addon) => (
						<Card key={addon.id} size="small" style={{ marginBottom: 16 }}>
							<Row gutter={16} style={{ marginBottom: 12 }}>
								<Col span={4}>
									<Input
										placeholder="Add-on code"
										value={addon.code}
										onChange={(e) => handleAddon.update(addon.id, "code", e.target.value)}
									/>
								</Col>
								<Col span={5}>
									<Input
										placeholder="Add-on name"
										value={addon.name}
										onChange={(e) => handleAddon.update(addon.id, "name", e.target.value)}
									/>
								</Col>
								<Col span={6}>
									<Input
										placeholder="Description"
										value={addon.description}
										onChange={(e) => handleAddon.update(addon.id, "description", e.target.value)}
									/>
								</Col>
								<Col span={3}>
									<Select
										value={addon.status}
										onChange={(value) => handleAddon.update(addon.id, "status", value)}
										style={{ width: "100%" }}>
										{STATUS_OPTIONS.map((option) => (
											<Option key={option.value} value={option.value}>
												{option.label}
											</Option>
										))}
									</Select>
								</Col>
								<Col span={4}>
									<InputNumber
										placeholder="Price"
										value={addon.priceAmount}
										onChange={(value) => handleAddon.update(addon.id, "priceAmount", value || 0)}
										style={{ width: "100%" }}
										min={0}
									/>
								</Col>
								<Col span={2}>
									<Button
										type="text"
										danger
										icon={<DeleteOutlined />}
										onClick={() => handleAddon.remove(addon.id)}
									/>
								</Col>
							</Row>
							<Row gutter={16}>
								<Col span={6}>
									<Select
										placeholder="Price unit"
										value={addon.priceUnit}
										onChange={(value) => handleAddon.update(addon.id, "priceUnit", value)}
										style={{ width: "100%" }}>
										{PRICE_UNIT_OPTIONS.map((option) => (
											<Option key={option.value} value={option.value}>
												{option.label}
											</Option>
										))}
									</Select>
								</Col>
								<Col span={6}>
									<Select
										placeholder="Price type"
										value={addon.priceType}
										onChange={(value) => handleAddon.update(addon.id, "priceType", value)}
										style={{ width: "100%" }}>
										{PRICE_TYPE_OPTIONS.map((option) => (
											<Option key={option.value} value={option.value}>
												{option.label}
											</Option>
										))}
									</Select>
								</Col>
							</Row>
						</Card>
					))}
					{addons.length === 0 && <Empty description="No add-ons added yet" />}
				</Card>
			),
		},
	];

	return (
		<div className="page-content">
			<div style={{ marginBottom: 24 }}>
				<Space>
					<Button icon={<PiArrowLeftDuotone />} onClick={() => navigate("/services")}></Button>
					<Title level={4} style={{ margin: 0 }}>
						Add New Service
					</Title>
				</Space>
			</div>
			<div className="page-content-wrapper">
				{/* <Breadcrumb
					style={{
						margin: "16px 0",
					}}>
					<Breadcrumb.Item>
						<Link to="/dashboard">Dashboard</Link>
					</Breadcrumb.Item>
					<Breadcrumb.Item>
						<Link to="/services">Services</Link>
					</Breadcrumb.Item>
					<Breadcrumb.Item>Add Service</Breadcrumb.Item>
				</Breadcrumb> */}

				<Card
					title="Add Service"
					extra={
						<div>
							<Button onClick={() => navigate("/services")} style={{ marginRight: 8 }}>
								Cancel
							</Button>
							<Button type="primary" loading={loading} onClick={handleSubmit}>
								Add Service
							</Button>
						</div>
					}>
					<Form form={form} layout="vertical">
						<Tabs items={tabItems} />
					</Form>
				</Card>
			</div>
		</div>
	);
}

export default AddServicePage;
